import { ApolloError, gql, QueryResult, QueryTuple, useLazyQuery, useQuery } from '@apollo/client';

import { client } from '../../../../apollo/ApolloConfig';
import { getPolices } from '../../../../utils/gqlPolicesUtil';

export const JOB_POSTING_RECOMMENDATIONS = gql`
  query GetJobPostingRecommendations($jobTitleId: NonEmptyString) {
    getJobPostingRecommendations(jobTitleId: $jobTitleId) {
      id
      companyName
      companyImageUrl
      publishedDate
      jobTitle
      jobDescription
      url
      skills {
        skillId
        translatedName
        name
        type
        translatedType
        seniorityRequirements {
          seniorityId
          requiredLevel
        }
        skillProficiencyLevel
      }
    }
  }
`;

export interface ISkillInJobPostingRecommendation {
  skillId: string;
  translatedName: string;
  name: string;
  type: string;
  translatedType: string;
  seniorityRequirements: {
    seniorityId: string;
    requiredLevel: number;
  }[];
  skillProficiencyLevel?: number;
}

export interface IJobPostingRecommendation {
  id: string;
  companyName: string;
  companyImageUrl?: string;
  publishedDate: string;
  jobTitle: string;
  jobDescription: string;
  url: string;
  skills: ISkillInJobPostingRecommendation[];
}

interface IGetJobPostingRecommendationsResponse {
  getJobPostingRecommendations: IJobPostingRecommendation[];
}

interface IGetJobPostingRecommendationsVariables {
  jobTitleId?: string;
}

export interface IGetJobPostingRecommendationsRefetchResponse {
  data: IGetJobPostingRecommendationsResponse;
  error: ApolloError | undefined;
}

export const useQueryGetJobPostingRecommendations = (
  fromCache?: boolean,
  jobTitleId?: string,
): QueryResult<IGetJobPostingRecommendationsResponse, IGetJobPostingRecommendationsVariables> =>
  useQuery<IGetJobPostingRecommendationsResponse, IGetJobPostingRecommendationsVariables>(JOB_POSTING_RECOMMENDATIONS, {
    ...getPolices(fromCache),
    variables: jobTitleId ? { jobTitleId } : undefined,
  });

export const useLazyGetJobPostingRecommendations = (
  fromCache?: boolean,
): QueryTuple<IGetJobPostingRecommendationsResponse, IGetJobPostingRecommendationsVariables> =>
  useLazyQuery<IGetJobPostingRecommendationsResponse, IGetJobPostingRecommendationsVariables>(
    JOB_POSTING_RECOMMENDATIONS,
    { ...getPolices(fromCache) },
  );

export const getJobPostingRecommendationsRefetch = async (
  jobTitleId?: string,
): Promise<IGetJobPostingRecommendationsRefetchResponse> => {
  const data = await client.query({
    query: JOB_POSTING_RECOMMENDATIONS,
    variables: jobTitleId ? { jobTitleId } : undefined,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return {
    data: data.data,
    error: data?.error,
  };
};
